import React, { FunctionComponent } from 'react'
import { View } from 'react-native'

import { useTheme } from 'styled-components/native'

import {
  MicrositeConfig,
  MicrositeConfigSectionNames,
} from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'
import { useGetMicrositeSections } from '../../hooks/useGetMicrositeSections'
import { AuroraSectionDivider } from '../../organisms/auroraSectionDivider/AuroraSectionDivider'
import { AuroraSectionGradientWrapper } from '../../organisms/auroraSectionGradientWrapper/AuroraSectionGradientWrapper'
import {
  LegalAndCopyrightFooter,
  LegalAndCopyrightFooterProps,
} from '../../organisms/legalAndCopyrightFooter/LegalAndCopyrightFooter'
import { LyraMembershipSummaryProps } from '../../organisms/lyraMembershipSummary/LyraMembershipSummary'
import { LyraOfferingsCarouselProps } from '../../organisms/lyraOfferingsCarousel/LyraOfferingsCarousel'
import { MicrositeCustomerBrandedBannerProps } from '../../organisms/micrositeCustomerBrandedBanner/MicrositeCustomerBrandedBanner'
import { MicrositeMobileAppPromoBannerProps } from '../../organisms/micrositeMobileAppPromo/MicrositeMobileAppPromoBanner'
import { MicrositeMobileAppPromoSectionProps } from '../../organisms/micrositeMobileAppPromo/MicrositeMobileAppPromoSection'
import { PathwaysCareNavigatorSectionProps } from '../../organisms/pathwaysCareNavigatorSection/PathwaysCareNavigatorSection'
import { PathwaysFAQSectionProps } from '../../organisms/pathwaysFAQSection/PathwaysFAQSection'
import { PathwaysHomepageHeroSectionProps } from '../../organisms/PathwaysHomepageHeroSection/PathwaysHomepageHeroSection'
import { PathwaysSelfCareSectionProps } from '../../organisms/pathwaysSelfCareSection/PathwaysSelfCareSection'

export interface PathwaysMicrositeHomePageProps {
  heroSectionProps: PathwaysHomepageHeroSectionProps
  faqSectionProps: PathwaysFAQSectionProps
  lyraOfferingsSectionProps: LyraOfferingsCarouselProps
  selfCareSectionProps: PathwaysSelfCareSectionProps
  careNavigatorSectionProps: PathwaysCareNavigatorSectionProps
  legalAndCopyrightFooterProps: LegalAndCopyrightFooterProps
  membershipSectionProps: LyraMembershipSummaryProps
  mobileAppPromoSectionProps: MicrositeMobileAppPromoSectionProps
  mobileAppPromoBannerProps: MicrositeMobileAppPromoBannerProps
  customerBrandedBannerProps: MicrositeCustomerBrandedBannerProps
  micrositeConfig: MicrositeConfig
}

export const PathwaysMicrositeHomePage: FunctionComponent<PathwaysMicrositeHomePageProps> = ({
  heroSectionProps,
  faqSectionProps,
  lyraOfferingsSectionProps,
  selfCareSectionProps,
  careNavigatorSectionProps,
  legalAndCopyrightFooterProps,
  micrositeConfig,
  membershipSectionProps,
  mobileAppPromoSectionProps,
  mobileAppPromoBannerProps,
  customerBrandedBannerProps,
}) => {
  const sectionToPropsMap = {
    [MicrositeConfigSectionNames.HERO]: heroSectionProps,
    [MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER]: mobileAppPromoBannerProps,
    [MicrositeConfigSectionNames.CUSTOMER_BRANDED_BANNER]: customerBrandedBannerProps,
    [MicrositeConfigSectionNames.OFFERINGS_CAROUSEL]: {
      ...lyraOfferingsSectionProps,
      ...micrositeConfig[MicrositeConfigSectionNames.OFFERINGS_CAROUSEL],
    },
    [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: mobileAppPromoSectionProps,
    [MicrositeConfigSectionNames.SELF_CARE]: selfCareSectionProps,
    [MicrositeConfigSectionNames.TESTIMONIALS]: {},
    [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: membershipSectionProps,
    [MicrositeConfigSectionNames.CARE_NAVIGATOR]: careNavigatorSectionProps,
    [MicrositeConfigSectionNames.FAQ]: faqSectionProps,
  }

  const sectionsDisplayed = useGetMicrositeSections(micrositeConfig)
  const { isAuroraTheme } = useTheme() as ThemeType

  const sectionsWithDividerAfter = new Set([
    MicrositeConfigSectionNames.OFFERINGS_CAROUSEL,
    MicrositeConfigSectionNames.MOBILE_APP_PROMO,
    MicrositeConfigSectionNames.SELF_CARE,
    MicrositeConfigSectionNames.TESTIMONIALS,
    MicrositeConfigSectionNames.LYRA_MEMBERSHIP,
  ])

  const renderSection = (sectionName: string, Component: React.ComponentType, sectionProps: object) => {
    return <Component {...sectionProps} />
  }

  const renderSectionWithDivider = (section: (typeof sectionsDisplayed)[0]) => {
    const { name: sectionName, component: Component, backgroundColor } = section
    const sectionProps = { ...sectionToPropsMap[sectionName], ...(backgroundColor && { backgroundColor }) }
    const showDivider = isAuroraTheme && sectionsWithDividerAfter.has(sectionName)

    return (
      <React.Fragment key={sectionName}>
        {renderSection(sectionName, Component, sectionProps)}
        {showDivider && <AuroraSectionDivider />}
      </React.Fragment>
    )
  }

  // Find the indices to split sections for Aurora gradient
  const firstGradientIndex = sectionsDisplayed.findIndex((s) => s.useAuroraGradient)
  const lastGradientIndex = sectionsDisplayed.map((s) => s.useAuroraGradient).lastIndexOf(true)
  const hasGradientSections = firstGradientIndex >= 0

  // Sections before the gradient (e.g., Hero, Mobile App Promo Banner)
  const sectionsBeforeGradient = hasGradientSections ? sectionsDisplayed.slice(0, firstGradientIndex) : []

  // Sections inside the gradient
  const gradientSections = hasGradientSections ? sectionsDisplayed.slice(firstGradientIndex, lastGradientIndex + 1) : []

  // Sections after the gradient (e.g., FAQ)
  const sectionsAfterGradient = hasGradientSections ? sectionsDisplayed.slice(lastGradientIndex + 1) : []

  // When Aurora is not enabled, render all sections normally
  if (!isAuroraTheme) {
    return (
      <View testID={tID('PathwaysMicrositeHomePage')}>
        {sectionsDisplayed.map(renderSectionWithDivider)}
        <LegalAndCopyrightFooter {...legalAndCopyrightFooterProps} />
      </View>
    )
  }

  return (
    <View testID={tID('PathwaysMicrositeHomePage')}>
      {/* Render sections before gradient (e.g., Hero) */}
      {sectionsBeforeGradient.map(renderSectionWithDivider)}

      {/* Wrap all gradient sections in a single gradient wrapper */}
      {gradientSections.length > 0 && (
        <AuroraSectionGradientWrapper>{gradientSections.map(renderSectionWithDivider)}</AuroraSectionGradientWrapper>
      )}

      {/* Render sections after gradient (e.g., FAQ) */}
      {sectionsAfterGradient.map(renderSectionWithDivider)}

      {/* Footer at the bottom */}
      <LegalAndCopyrightFooter {...legalAndCopyrightFooterProps} />
    </View>
  )
}
